import React from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { FaClock, FaMapMarkerAlt, FaPlane, FaRoute, FaTags } from "react-icons/fa";
import { getDestinationHeroImage } from "../data/destinationHeroImages";
import { trackBookingClick, trackWhatsAppClick } from "../utils/analytics";

const whatsappLink =
  "https://wa.me/35796613191?text=Hello,%20I%20would%20like%20to%20book%20a%20taxi.";

const internalLinks = [
  ["Home", "/"],
  ["Larnaca Airport Taxi", "/larnaca-airport-taxi"],
  ["Paphos Airport Taxi", "/paphos-airport-taxi"],
  ["Taxi Limassol", "/taxi-limassol"],
  ["Cyprus Airport Transfers", "/cyprus-airport-transfers"],
];

function SectionHeader({ eyebrow, title, text }) {
  return (
    <div className="mb-12 max-w-3xl">
      <p className="text-sm font-bold uppercase tracking-[0.18em] text-yellow-300">{eyebrow}</p>
      <h2 className="mt-4 text-3xl font-black tracking-tight text-white md:text-5xl">
        {title}
      </h2>
      {text && <p className="mt-5 leading-8 text-neutral-300">{text}</p>}
    </div>
  );
}

const cardClass =
  "rounded-[2rem] border border-white/10 bg-gradient-to-br from-white/[0.08] to-white/[0.025] p-6 shadow-xl shadow-black/25 transition-all duration-300 hover:-translate-y-1 hover:border-yellow-400/50 hover:shadow-yellow-400/10";

const primaryButtonClass =
  "inline-flex items-center justify-center rounded-2xl bg-yellow-400 px-8 py-4 text-center font-extrabold text-black shadow-xl shadow-yellow-400/20 transition-all duration-200 hover:-translate-y-1 hover:bg-yellow-300 hover:shadow-yellow-400/30";

const secondaryButtonClass =
  "inline-flex items-center justify-center rounded-2xl border border-yellow-400/25 bg-white/[0.06] px-8 py-4 text-center font-extrabold text-white backdrop-blur transition-all duration-200 hover:-translate-y-1 hover:border-yellow-400/60 hover:text-yellow-300";

function SeoLandingPage({ page }) {
  const heroImage = getDestinationHeroImage(page.heroImageKey || page.h1);
  const detailLookup = Object.fromEntries(
    page.infoDetails.map(([label, value]) => [label.toLowerCase(), value])
  );
  const defaultRouteInfoCards = [
    {
      label: "Distance",
      value: detailLookup.route || detailLookup.location || detailLookup.airports || "Direct route",
      icon: FaRoute,
    },
    {
      label: "Travel Time",
      value: detailLookup["journey time"] || "Route dependent",
      icon: FaClock,
    },
    {
      label: "From Price",
      value: page.snapshotValue,
      icon: FaTags,
    },
    {
      label: "Availability",
      value: detailLookup.availability || "24/7 by request",
      icon: FaPlane,
    },
  ];
  const heroInfoIcons = [FaMapMarkerAlt, FaTags, FaPlane, FaClock];
  const routeInfoCards = page.heroInfoCards
    ? page.heroInfoCards.map((item, index) => ({
        ...item,
        icon: heroInfoIcons[index % heroInfoIcons.length],
      }))
    : defaultRouteInfoCards;

  return (
    <main className="min-h-screen overflow-hidden bg-[#030712] text-white">
      <Helmet>
        <title>{page.title}</title>
        <meta name="description" content={page.description} />
        <meta name="robots" content="index, follow" />
        <meta property="og:title" content={page.title} />
        <meta property="og:description" content={page.description} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={page.canonical} />
        <meta
          property="og:image"
          content="https://limassolairporttaxi.com/og-image.jpg"
        />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={page.title} />
        <meta name="twitter:description" content={page.description} />
        <meta
          name="twitter:image"
          content="https://limassolairporttaxi.com/og-image.jpg"
        />
        <link rel="canonical" href={page.canonical} />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: page.faqs.map((faq) => ({
              "@type": "Question",
              name: faq.question,
              acceptedAnswer: {
                "@type": "Answer",
                text: faq.answer,
              },
            })),
          })}
        </script>
      </Helmet>

      <section
        className="relative overflow-hidden bg-[#030712] px-6 pb-20 pt-36 md:pb-28 md:pt-44"
      >
        <img
          src={heroImage}
          alt=""
          className="absolute inset-0 h-full w-full object-cover object-center"
          loading="eager"
          fetchPriority="high"
          decoding="async"
        />
        <div className="absolute inset-0 bg-black/15" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_22%_18%,rgba(250,204,21,0.12),transparent_34%),linear-gradient(115deg,rgba(0,0,0,0.5),rgba(0,0,0,0.35)_48%,rgba(0,0,0,0.5))]" />
        <div className="absolute bottom-0 left-0 right-0 h-28 bg-gradient-to-t from-[#030712]/95 to-transparent" />

        <div className="relative mx-auto max-w-7xl">
          <nav className="mb-8 flex flex-wrap items-center gap-2 text-sm text-neutral-300">
            {page.breadcrumbs.map((item, index) => (
              <React.Fragment key={item.label}>
                {index > 0 && <span className="text-neutral-500">&gt;</span>}
                {item.to ? (
                  <Link to={item.to} className="hover:text-yellow-300">
                    {item.label}
                  </Link>
                ) : (
                  <span className="text-yellow-300">{item.label}</span>
                )}
              </React.Fragment>
            ))}
          </nav>

          <div className="grid items-center gap-10 lg:grid-cols-[1.08fr_0.92fr]">
            <div>
              <p className="inline-flex rounded-full border border-yellow-400/30 bg-yellow-400/10 px-5 py-2 text-sm font-bold uppercase tracking-[0.18em] text-yellow-300 shadow-lg shadow-yellow-400/10">
                Limassol Airport Taxi
              </p>

              <h1 className="mt-6 text-4xl font-black leading-[1.02] tracking-tight text-white md:text-7xl">
                {page.h1}
              </h1>

              <p className="mt-6 max-w-3xl text-lg leading-8 text-neutral-200">
                {page.heroText}
              </p>

              <div className="mt-8 flex flex-col gap-4 sm:flex-row">
                <a
                  href="/#booking"
                  onClick={() => trackBookingClick("seo_hero")}
                  className={primaryButtonClass}
                >
                  Book Taxi Now
                </a>

                <a
                  href={whatsappLink}
                  onClick={() => trackWhatsAppClick("seo_hero")}
                  target="_blank"
                  rel="noreferrer"
                  className={secondaryButtonClass}
                >
                  WhatsApp Booking
                </a>
              </div>
            </div>

            <aside className="rounded-[2rem] border border-yellow-400/25 bg-gradient-to-br from-white/[0.11] via-white/[0.05] to-yellow-400/[0.035] p-6 shadow-2xl shadow-black/35 backdrop-blur">
              <p className="text-sm font-semibold uppercase tracking-[0.18em] text-neutral-400">
                Transfer snapshot
              </p>
              <div className="mt-5 rounded-[1.5rem] border border-yellow-400/35 bg-yellow-400 p-7 text-black shadow-xl shadow-yellow-400/20">
                <p className="text-neutral-400">{page.snapshotLabel}</p>
                <p className="mt-3 text-4xl font-black text-black md:text-5xl">
                  {page.snapshotValue}
                </p>
                <p className="mt-4 leading-7 text-black/75">
                  {page.snapshotText}
                </p>
              </div>
              <div className="mt-5 grid gap-3 sm:grid-cols-2">
                {routeInfoCards.map((item) => {
                  const Icon = item.icon;

                  return (
                    <div
                      key={item.label}
                      className="rounded-2xl border border-white/10 bg-black/25 p-4 shadow-lg shadow-black/10"
                    >
                      <div className="mb-3 flex h-9 w-9 items-center justify-center rounded-xl bg-yellow-400/10 text-yellow-300">
                        <Icon />
                      </div>
                      <p className="text-xs font-bold uppercase tracking-[0.16em] text-yellow-300">
                        {item.label}
                      </p>
                      <p className="mt-2 text-sm font-bold leading-6 text-white">
                        {item.value}
                      </p>
                    </div>
                  );
                })}
              </div>
            </aside>
          </div>
        </div>
      </section>

      <section className="relative mx-auto max-w-7xl px-6 py-20 md:py-24">
        <div className="grid gap-10 lg:grid-cols-[0.86fr_1.14fr] lg:items-start">
          <SectionHeader
            eyebrow="Overview"
            title={page.overviewTitle}
            text={page.overviewText}
          />
        <div className="grid gap-5 md:grid-cols-2">
          {page.overviewCards.map((card) => (
            <article
              key={card.title}
              className={cardClass}
            >
              <p className="text-sm font-semibold uppercase tracking-[0.16em] text-yellow-300">
                {card.label}
              </p>
              <h3 className="mt-4 text-2xl font-bold text-white">
                {card.title}
              </h3>
              <p className="mt-4 leading-7 text-neutral-400">{card.text}</p>
              {(card.bestFor || card.priceRange) && (
                <div className="mt-5 grid gap-3 border-t border-white/10 pt-5">
                  {card.bestFor && (
                    <div>
                      <p className="text-xs font-bold uppercase tracking-[0.16em] text-yellow-300">
                        Best for
                      </p>
                      <p className="mt-1 text-sm leading-6 text-neutral-300">
                        {card.bestFor}
                      </p>
                    </div>
                  )}
                  {card.priceRange && (
                    <div>
                      <p className="text-xs font-bold uppercase tracking-[0.16em] text-yellow-300">
                        Expected price range
                      </p>
                      <p className="mt-1 text-sm font-bold text-white">
                        {card.priceRange}
                      </p>
                    </div>
                  )}
                </div>
              )}
            </article>
          ))}
          </div>
        </div>
      </section>

      <section className="relative bg-[#09090b] px-6 py-20 md:py-24">
        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-yellow-400/20 to-transparent" />
        <div className="mx-auto max-w-7xl">
          <SectionHeader
            eyebrow={page.whyEyebrow || "Why choose us"}
            title={page.whyTitle}
            text={page.whyText}
          />
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {page.whyItems.map((item) => (
              <div
                key={item}
                className={cardClass}
              >
                <div className="mb-5 flex h-11 w-11 items-center justify-center rounded-2xl bg-yellow-400 font-bold text-black shadow-lg shadow-yellow-400/20">
                  +
                </div>
                <p className="font-semibold leading-7 text-neutral-200">
                  {item}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-20 md:py-24">
        <SectionHeader
          eyebrow={page.routesEyebrow || "Popular routes"}
          title={page.routesTitle}
          text={page.routesText}
        />
        <div className="grid gap-4 md:grid-cols-2">
          {page.popularRoutes.map((route) => (
            <Link
              key={route.to}
              to={route.to}
              className="group rounded-[2rem] border border-white/10 bg-gradient-to-br from-white/[0.08] to-white/[0.025] p-6 shadow-xl shadow-black/20 transition-all duration-300 hover:-translate-y-1 hover:border-yellow-400/50"
            >
              <div className="mb-5 flex h-11 w-11 items-center justify-center rounded-2xl bg-yellow-400/10 text-yellow-300 transition duration-200 group-hover:bg-yellow-400 group-hover:text-black">
                <FaMapMarkerAlt />
              </div>
              <h3 className="text-2xl font-black text-white">
                {route.title}
              </h3>
              <p className="mt-3 leading-7 text-neutral-400">{route.text}</p>
            </Link>
          ))}
        </div>
      </section>

      <section className="relative bg-[#09090b] px-6 py-20 md:py-24">
        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-yellow-400/20 to-transparent" />
        <div className="mx-auto grid max-w-7xl gap-8 lg:grid-cols-[0.85fr_1.15fr]">
          <div>
            <p className="text-sm font-bold uppercase tracking-[0.18em] text-yellow-300">
              Airport transfer information
            </p>
            <h2 className="mt-4 text-3xl font-black tracking-tight text-white md:text-5xl">
              {page.infoTitle}
            </h2>
            <p className="mt-5 leading-8 text-neutral-300">{page.infoText}</p>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            {page.infoDetails.map(([label, value]) => (
              <div
                key={label}
                className={cardClass}
              >
                <p className="text-sm text-neutral-400">{label}</p>
                <p className="mt-2 font-bold text-white">{value}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-20 md:py-24">
        <div className="rounded-[2rem] border border-yellow-400/25 bg-gradient-to-br from-yellow-400/20 via-white/[0.055] to-black/30 p-8 text-center shadow-2xl shadow-yellow-400/10 md:p-12">
          <p className="text-sm font-bold uppercase tracking-[0.18em] text-yellow-300">
            24/7 availability
          </p>
          <h2 className="mx-auto mt-4 max-w-3xl text-3xl font-black tracking-tight text-white md:text-5xl">
            {page.availabilityTitle}
          </h2>
          <p className="mx-auto mt-5 max-w-3xl leading-8 text-neutral-300">
            {page.availabilityText}
          </p>
          <div className="mt-8 flex flex-col justify-center gap-4 sm:flex-row">
            <a
              href="/#booking"
              onClick={() => trackBookingClick("seo_availability")}
              className={primaryButtonClass}
            >
              Book Taxi Now
            </a>
            <a
              href={whatsappLink}
              onClick={() => trackWhatsAppClick("seo_availability")}
              target="_blank"
              rel="noreferrer"
              className={secondaryButtonClass}
            >
              WhatsApp Booking
            </a>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 pb-20">
        <SectionHeader
          eyebrow="Useful links"
          title="Plan your taxi transfer across the site"
          text="Move between the main booking pages for airport transfers, city taxis, and Cyprus travel routes."
        />
        <div className="flex flex-wrap gap-3">
          {internalLinks.map(([label, to]) => (
            <Link
              key={to}
              to={to}
              className="rounded-2xl border border-white/10 bg-white/[0.04] px-5 py-3 font-semibold text-neutral-200 shadow-lg shadow-black/10 transition-all duration-200 hover:-translate-y-0.5 hover:border-yellow-400/50 hover:text-yellow-300"
            >
              {label}
            </Link>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-20 md:py-24">
        <div className="rounded-[2rem] border border-yellow-400/25 bg-gradient-to-br from-yellow-400/20 via-white/[0.055] to-black/30 p-8 text-center shadow-2xl shadow-yellow-400/10 md:p-12">
          <p className="text-sm font-bold uppercase tracking-[0.18em] text-yellow-300">
            Fast confirmation
          </p>
          <h2 className="mx-auto mt-4 max-w-3xl text-3xl font-black tracking-tight text-white md:text-5xl">
            Book Your Taxi Transfer Today
          </h2>
          <p className="mx-auto mt-5 max-w-2xl text-lg leading-8 text-neutral-300">
            Get fast confirmation by phone or WhatsApp with fixed prices and 24/7 availability.
          </p>
          <div className="mt-8 flex flex-col justify-center gap-4 sm:flex-row">
            <a
              href="/#booking"
              onClick={() => trackBookingClick("seo_final_cta")}
              className={primaryButtonClass}
            >
              Book Taxi Now
            </a>
            <a
              href={whatsappLink}
              onClick={() => trackWhatsAppClick("seo_final_cta")}
              target="_blank"
              rel="noreferrer"
              className={secondaryButtonClass}
            >
              WhatsApp Booking
            </a>
          </div>
        </div>
      </section>

      <section className="relative bg-[#09090b] px-6 py-20 md:py-24">
        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-yellow-400/20 to-transparent" />
        <div className="mx-auto max-w-5xl">
          <div className="mb-10 text-center">
            <p className="text-sm font-bold uppercase tracking-[0.18em] text-yellow-300">FAQ</p>
            <h2 className="mt-4 text-3xl font-black tracking-tight text-white md:text-5xl">
              {page.faqTitle}
            </h2>
          </div>

        <div className="grid gap-5">
            {page.faqs.map((faq) => (
              <article
                key={faq.question}
                className="rounded-[1.75rem] border border-white/10 bg-gradient-to-br from-white/[0.08] to-white/[0.025] p-6 shadow-xl shadow-black/20 md:p-7"
              >
                <h3 className="flex gap-3 text-xl font-black text-yellow-300">
                  <span className="mt-1 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-yellow-400 text-xs font-black text-black">
                    ?
                  </span>
                  <span>
                  {faq.question}
                  </span>
                </h3>
                <p className="mt-4 leading-7 text-neutral-400 md:pl-9">
                  {faq.answer}
                </p>
              </article>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}

export default SeoLandingPage;
